import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

export default function VerifyReceipt() {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const rid = new URLSearchParams(window.location.search).get("r") || "";

  useEffect(() => {
    if (!rid) { setState({ loading: false, data: null, error: "No receipt id provided." }); return; }
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/verify/receipt/${encodeURIComponent(rid)}`)
      .then(async (r) => ({ ok: r.ok, body: await r.json() }))
      .then(({ body }) => setState({ loading: false, data: body, error: body.valid === undefined && body.reason ? body.reason : "" }))
      .catch((e) => setState({ loading: false, data: null, error: e.message }));
  }, [rid]);

  const { loading, data, error } = state;
  const valid = data?.valid === true;
  const Row = ({ k, v, mono }) => (
    <div className="flex flex-col gap-0.5 border-b border-neutral-100 py-3 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-sm font-semibold text-neutral-500">{k}</span>
      <span className={`text-sm text-[#0A234A] ${mono ? "break-all font-mono text-xs" : "font-semibold"}`} data-testid={`verify-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F6F9] py-10 px-4" data-testid="verify-receipt-page">
      <div className="mx-auto max-w-2xl">
        <div className="mb-6 flex items-center gap-2">
          <span className="font-display text-xl font-extrabold tracking-tight text-[#0A234A]">FleetPods</span>
          <span className="rounded-full bg-[#0A234A] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Public verification</span>
        </div>

        {loading ? (
          <div className="flex items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-8 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /> Verifying receipt…</div>
        ) : error || !data ? (
          <div className="rounded-2xl border border-red-200 bg-white p-8" data-testid="verify-error">
            <ShieldAlert className="mb-3 h-8 w-8 text-red-500" />
            <p className="font-display text-lg font-bold text-[#0A234A]">Could not verify</p>
            <p className="mt-1 text-sm text-neutral-500">{error || "Receipt not found."}</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
            <div className={`flex items-center gap-3 p-6 ${valid ? "bg-emerald-50" : "bg-red-50"}`} data-testid="verify-verdict">
              {valid ? <ShieldCheck className="h-9 w-9 text-emerald-600" /> : <ShieldAlert className="h-9 w-9 text-red-600" />}
              <div>
                <p className={`font-display text-xl font-extrabold ${valid ? "text-emerald-700" : "text-red-700"}`}>{valid ? "Receipt is authentic" : "Verification failed — possible tampering"}</p>
                <p className="text-sm text-neutral-600">{data.algorithm}</p>
              </div>
            </div>
            <div className="p-6">
              <Row k="Receipt ID" v={data.receipt_id} mono />
              <Row k="Chain index" v={`#${data.chain_index}`} />
              <Row k="Subject" v={data.subject_ref} />
              <Row k="Jurisdiction" v={data.jurisdiction} />
              <Row k="Issued at" v={data.issued_at ? new Date(data.issued_at).toLocaleString() : "—"} />
              <Row k="Data provenance" v={data.provenance} />
              <Row k="Hash match" v={data.hash_match ? "✓ match" : "✗ mismatch"} />
              <Row k="Chain link" v={data.chain_link_ok ? "✓ linked" : "✗ broken"} />
              <Row k="Issuer signature" v={data.signature_valid ? "✓ valid — signed by FleetPods" : "✗ invalid / unsigned"} />
              <Row k="Content hash" v={data.content_hash} mono />
              <Row k="Recomputed" v={data.recomputed_hash} mono />
              <Row k="Previous hash" v={data.prev_hash} mono />
              {data.signature && <Row k="Signature (ECDSA P-256)" v={data.signature} mono />}
              {data.report_summary && (
                <div className="mt-4 rounded-xl bg-neutral-50 p-4 text-sm text-neutral-600">
                  <p className="mb-1 font-semibold text-[#0A234A]">Report summary</p>
                  <p>Telematics source: <span className="font-semibold">{data.report_summary.telematics_source ?? "—"}</span> · HOS: <span className="font-semibold">{data.report_summary.hos_state ?? "—"}</span> · Corridor: <span className="font-semibold">{data.report_summary.last_corridor ?? "—"}</span></p>
                </div>
              )}
              <p className="mt-4 text-xs text-neutral-400">This page recomputes the SHA-256 hash of the receipt's report and checks it links to the previous receipt in the append-only chain. A mismatch means the record was altered after issuance.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
